import React, { useState, useEffect, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, BarChart, Bar, ResponsiveContainer } from 'recharts';
import Sidebar from '../Components/Sidebar.tsx';
import axios from 'axios';

// Dummy data initially
const dummyLineData: { date: string; value: number }[] = [];
const dummyBarData: { month: string; generate: number; scan: number }[] = [];
const topInfluencers: { name: string; mobile: string; state: string; points: number }[] = [];

const chartTitle: React.CSSProperties = { 
  fontWeight: 700, 
  marginBottom: 16, 
  color: '#1a237e', 
  fontSize: 18,
  letterSpacing: '-0.3px'
};

const noDataStyle: React.CSSProperties = { 
  color: '#94a3b8', 
  textAlign: 'center' as const, 
  padding: '40px 0 30px', 
  fontSize: 16,
  fontWeight: 500
};

const theadCell: React.CSSProperties = { 
  textAlign: 'left' as const, 
  padding: '10px 12px', 
  fontWeight: 600, 
  color: '#334155',
  fontSize: 14,
  backgroundColor: '#fafbff'
};

const tbodyCell: React.CSSProperties = { 
  padding: '11px 12px', 
  color: '#334155', 
  fontSize: 14,
  borderBottom: '1px solid #f1f5f9'
};

// Skeleton loader for cards
const CardSkeleton: React.FC = () => (
  <div style={{
    background: '#fff',
    borderRadius: 12,
    padding: 16,
    minWidth: 200,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 170,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    animation: 'pulse 1.5s infinite ease-in-out'
  }}>
    <div style={{ height: 20, background: '#f1f5f9', borderRadius: 4, marginBottom: 12 }} />
    {[...Array(4)].map((_, i) => (
      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ height: 16, width: '45%', background: '#f1f5f9', borderRadius: 3 }} />
        <div style={{ height: 16, width: 50, background: '#e2e8f0', borderRadius: 4 }} />
      </div>
    ))}
  </div>
);

const MultiValueCard: React.FC<{
  title: string,
  fields: { label: string, value: number | string, tagColor: string }[]
}> = ({ title, fields }) => (
  <div 
    style={{ 
      background: '#fff', 
      borderRadius: 12, 
      padding: 18, 
      minWidth: 200, 
      flex: 1, 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: 170, 
      boxShadow: '0 3px 12px rgba(0,0,0,0.06)',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'scale(1.03)';
      e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.1)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'scale(1)';
      e.currentTarget.style.boxShadow = '0 3px 12px rgba(0,0,0,0.06)';
    }}
  >
    <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 14, color: '#0f172a' }}>{title}</div>
    {fields.map((item, idx) => (
      <div key={idx} style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: 9,
        justifyContent: 'space-between'
      }}>
        <span style={{ color: '#475569', fontSize: 14, minWidth: 90, marginRight: 10 }}>{item.label}:</span>
        <span
          style={{
            background: item.tagColor,
            minWidth: 44,
            textAlign: 'center' as const,
            padding: '4px 14px',
            borderRadius: 8,
            color: '#fff',
            fontWeight: 700,
            fontSize: 15,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          {item.value !== '' ? item.value : <span style={{ fontSize: 13, color: '#f1f5f9' }}>—</span>}
        </span>
      </div>
    ))}
    <div style={{ flex: 1 }} />
  </div>
);

// 🔍 Normalize profession to category
const getProfessionCategory = (profession: string | null): 'carpenter' | 'shop' | 'retailer' | null => {
  if (!profession) return null;
  const lower = profession.toLowerCase().trim();

  if (lower.includes('carpenter')) return 'carpenter';
  if (
    lower.includes('shop') || 
    lower.includes('store') ||
    lower.includes('boutique') ||
    lower.includes('tailor') ||
    lower.includes('electrician') ||
    lower.includes('furniture')
  ) return 'shop';
  if (
    lower.includes('retailer') ||
    lower.includes('distributor') ||
    lower.includes('wholesale') ||
    lower.includes('supplier')
  ) return 'retailer';

  return null;
};

const DashboardScreen: React.FC = () => {
  const [influencerData, setInfluencerData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [adminData, setAdminData] = useState<{ FullName: string; ProfilePhoto: string | null } | null>(null);

  useEffect(() => {
    const fetchInfluencers = async () => {
      try {
        const response = await axios.get("http://kkk.biznweb.in:5000/api/influencers");
        setInfluencerData(response.data.data || []);
      } catch (err) {
        console.error("Failed to fetch influencers", err);
      } finally {
        setLoading(false);
      }
    };
    fetchInfluencers();
  }, []);

  // Fetch admin details
  useEffect(() => {
    const fetchAdmin = async () => {
      try {
        const ADMIN_ID = '1'; // Replace with dynamic ID later
        const response = await fetch(`http://kkk.biznweb.in:5000/api/get-admin/${ADMIN_ID}`);
        if (!response.ok) throw new Error('Failed to fetch admin details');
        
        const data = await response.json();
        if (data.success && data.admin) {
          setAdminData({
            FullName: data.admin.FullName || 'Admin',
            ProfilePhoto: data.admin.ProfilePhoto || null
          });
        }
      } catch (err) {
        console.error("Failed to fetch admin", err);
        setAdminData({ FullName: 'Admin', ProfilePhoto: null });
      }
    };

    fetchAdmin();
  }, []);

  const counts = useMemo(() => {
    let carpenter = 0;
    let shop = 0;
    let retailer = 0;
    let pending = 0;
    let approved = 0;
    let rejected = 0;
    let suspect = 0;
    let kycApproved = 0;
    let kycNotApproved = 0;

    influencerData.forEach(inf => {
      const cat = getProfessionCategory(inf.Profession);
      if (cat === 'carpenter') carpenter++;
      else if (cat === 'shop') shop++;
      else if (cat === 'retailer') retailer++;

      const status = (inf.Status || '').toString().toLowerCase();
      if (status === 'pending') pending++;
      else if (status === 'reject' || status === 'rejected') rejected++;
      else if (status === 'suspect') suspect++;
      else if (status === 'approved') approved++;

      // ✅ KYC COUNTING
      if (inf.KYC === 'on') kycApproved++;
      else kycNotApproved++;
    });

    return {
      carpenter,
      shop,
      retailer,
      total: influencerData.length,
      pending,
      approved,
      rejected,
      suspect,
      kycApproved,
      kycNotApproved
    };
  }, [influencerData]);

  const totalInfluencerFields = [
    { label: 'Carpenter', value: counts.carpenter, tagColor: '#ef4444' },
    { label: 'Shops', value: counts.shop, tagColor: '#ec4899' },
    { label: 'Retailer', value: counts.retailer, tagColor: '#3b82f6' },
    { label: 'Total', value: counts.total, tagColor: '#1e40af' },
  ];

  const influencerStatusFields = [
    { label: 'Pending', value: counts.pending, tagColor: '#22c55e' },
    { label: 'Approved', value: counts.approved, tagColor: '#f59e0b' },
    { label: 'Rejected', value: counts.rejected, tagColor: '#ef4444' },
    { label: 'Suspect', value: counts.suspect, tagColor: '#6b7280' },
    { label: 'Total', value: counts.total, tagColor: '#1e40af' },
  ];

  // ✅ UPDATED WITH REAL KYC COUNTS
  const pendingKycFields = [
    { label: 'KYC Approved', value: counts.kycApproved, tagColor: '#22c55e' },
    { label: 'KYC Not Approved', value: counts.kycNotApproved, tagColor: '#978f81' },
    { label: 'Redeem Request', value: 0, tagColor: '#f59e0b' },
  ];

  const qrCodeFields = [
    { label: 'Total Generated', value: 0, tagColor: '#0ea5e9' },
    { label: 'Total Scanning', value: 0, tagColor: '#10b981' },
  ];

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8fafc' }}>
      <Sidebar />
      <div style={{ 
        flex: 1, 
        marginLeft: '280px', 
        height: '100vh', 
        overflowY: 'auto',
        padding: '0 16px'
      }}>
        {/* Header */}
        <div style={{ 
          background: '#fff', 
          padding: '18px 24px', 
          display: 'flex', 
          alignItems: 'center', 
          borderBottom: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
        }}>
          <h2 style={{ margin: 0, fontWeight: 700, fontSize: 22, color: '#1e40af' }}>Influencer Reward Dashboard</h2>
          
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16 }}>
            {/* Profile Section */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {adminData?.ProfilePhoto && (
                <img
                  src={adminData.ProfilePhoto}
                  alt="Profile"
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    border: '2px solid #e2e8f0',
                    objectFit: 'cover'
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = "/assets/default-profile.jpg";
                  }}
                />
              )}
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: '#475569', fontWeight: 600, fontSize: 15 }}>
                  Welcome: <span style={{ color: '#1e40af', fontWeight: 700 }}>{adminData?.FullName || 'Admin'}</span>
                </div>
              </div>
            </div>
            
            {/* 🔴 Logout Button */}
            <button
              onClick={() => {
                localStorage.removeItem("isLoggedIn");
                localStorage.removeItem("info");
                window.location.href = '/'; // hard redirect to ensure no cached state
              }}
              style={{
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '6px 14px',
                fontWeight: 600,
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#dc2626'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#ef4444'}
            >
              Logout
            </button>
          </div>
        </div>

        {/* Cards Section */}
        <div style={{ 
          padding: '24px 16px 16px', 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '18px', 
          justifyContent: 'center',
          rowGap: '24px'
        }}>
          {loading ? (
            <>
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
            </>
          ) : (
            <>
              <MultiValueCard title="Total Influencer" fields={totalInfluencerFields} />
              <MultiValueCard title="Influencer Status" fields={influencerStatusFields} />
              <MultiValueCard title="Pending KYC & Redeem Request" fields={pendingKycFields} />
              <MultiValueCard title="QR Codes" fields={qrCodeFields} />
            </>
          )}
        </div>

        {/* Charts */}
        <div style={{ 
          display: 'flex', 
          gap: 24, 
          padding: '0 16px 24px', 
          flexWrap: 'wrap',
          rowGap: '24px'
        }}>
          <div style={{ 
            background: '#fff', 
            flex: 1, 
            padding: 20, 
            borderRadius: 14, 
            minHeight: 290,
            minWidth: 320,
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            animation: loading ? 'none' : 'fadeInUp 0.5s ease forwards'
          }}>
            <div style={chartTitle}>Last 30 Days Scanning</div>
            {dummyLineData.length === 0 ? (
              <div style={noDataStyle}>No Data Available</div>
            ) : (
              <ResponsiveContainer width="100%" height={210}>
                <LineChart data={dummyLineData}>
                  <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                  <Tooltip 
                    contentStyle={{ 
                      borderRadius: 8, 
                      border: 'none', 
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      fontSize: 13 
                    }} 
                  />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#1e40af" 
                    strokeWidth={2}
                    dot={{ stroke: '#1e40af', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, stroke: '#fff', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
          <div style={{ 
            background: '#fff', 
            flex: 1, 
            padding: 20, 
            borderRadius: 14, 
            minHeight: 290,
            minWidth: 320,
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            animation: loading ? 'none' : 'fadeInUp 0.6s ease forwards'
          }}>
            <div style={chartTitle}>Last 6 Month Coupon Generate V/S Scanning</div>
            {dummyBarData.length === 0 ? (
              <div style={noDataStyle}>No Data Available</div>
            ) : (
              <ResponsiveContainer width="100%" height={210}>
                <BarChart data={dummyBarData}>
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                  <Tooltip 
                    contentStyle={{ 
                      borderRadius: 8, 
                      border: 'none', 
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      fontSize: 13 
                    }} 
                  />
                  <Bar dataKey="generate" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="scan" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Top Influencers */}
        <div style={{ padding: '0 16px 32px' }}>
          <div style={chartTitle}>Top 10 Influencers</div>
          <div style={{ 
            background: '#fff', 
            borderRadius: 12, 
            padding: 0, 
            minHeight: 150,
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            animation: loading ? 'none' : 'fadeInUp 0.7s ease forwards'
          }}>
            {topInfluencers.length === 0 ? (
              <div style={{ ...noDataStyle, padding: '32px' }}>No Influencer Data</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
                  <thead>
                    <tr>
                      <th style={theadCell}>Name</th>
                      <th style={theadCell}>Mobile No</th>
                      <th style={theadCell}>State</th>
                      <th style={theadCell}>Points</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topInfluencers.map((row, i) => (
                      <tr key={i} style={{ transition: 'background 0.2s' }}>
                        <td style={tbodyCell}>{row.name}</td>
                        <td style={tbodyCell}>{row.mobile}</td>
                        <td style={tbodyCell}>{row.state}</td>
                        <td style={tbodyCell}>{row.points}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Global Animations */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(15px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }
        @media (max-width: 768px) {
          div[style*="marginLeft: '280px'"] {
            margin-left: 0 !important;
            padding: 0 12px;
          }
          h2[style*="Influencer Reward Dashboard"] {
            font-size: 20px !important;
          }
        }
        @media (max-width: 600px) {
          div[style*="flexWrap: 'wrap'"] > div {
            min-width: 100% !important;
          }
        }
      `}</style>
    </div>
  );
};

export default DashboardScreen;